import { useState, useRef, useEffect } from "react";
import { sendChatMessage } from "../services/api";
import { useCart } from "../context/CartContext";
import { imageSrc } from "../utils/assets";
import styles from "./ChatWidget.module.css";

export default function ChatWidget() {
  const { addToCart } = useCart();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "👋 Hello and welcome to Atelier Noir! I'm your personal style concierge. How can I help you look your best today?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);

    try {
      const { reply, conversationId: cid, products } = await sendChatMessage(msg, conversationId);
      setConversationId(cid);
      setMessages((prev) => [...prev, { role: "bot", text: reply, products }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Sorry, I encountered an error. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        className={`${styles.fab} ${open ? styles.fabOpen : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Toggle chat"
      >
        {open ? "✕" : "💬"}
      </button>

      {open && (
        <div className={styles.widget}>
          <div className={styles.header}>
            <div className={styles.headerInfo}>
              <span className={styles.onlineDot} />
              <div>
                <p className={styles.headerTitle}>Atelier Noir Concierge</p>
                <p className={styles.headerSub}>Usually replies instantly</p>
              </div>
            </div>
            <button className={styles.closeBtn} onClick={() => setOpen(false)}>✕</button>
          </div>

          <div className={styles.messages}>
            {messages.map((m, i) => (
              <div key={i} className={styles.msgGroup}>
                <div className={`${styles.message} ${m.role === "user" ? styles.user : styles.bot}`}>
                  {m.text}
                </div>
                {m.products && m.products.length > 0 && (
                  <div className={styles.products}>
                    {m.products.map((p) => (
                      <div key={p.id} className={styles.prodCard}>
                        {p.image_url && (
                          <img className={styles.prodImg} src={imageSrc(p.image_url)} alt={p.name} loading="lazy" />
                        )}
                        <div className={styles.prodInfo}>
                          <p className={styles.prodName}>{p.name.split(" / ")[0]}</p>
                          <p className={styles.prodMeta}>
                            {p.color ? `${p.color} · ` : ""}EGP {Number(p.price).toLocaleString()}
                          </p>
                          <button
                            className={styles.prodBtn}
                            onClick={() => addToCart({ ...p, emoji: "" }, p.price)}
                          >
                            Add to Bag
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className={`${styles.message} ${styles.bot}`}>
                <span className="loading-dot" />
                <span className="loading-dot" />
                <span className="loading-dot" />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className={styles.inputRow}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Type your message…"
              disabled={loading}
            />
            <button className={styles.sendBtn} onClick={sendMessage} disabled={loading}>
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}